import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MessageTypeEnum, RCSData, RCSDataRichCard, RCSMessageTypeEnum } from 'lib-trend-core';

/*
 * The RCS message form shared by the campaign wizard and the webhook integration
 * (SPEC-2026-10-rcs-webhook-integration-messages, plan AD-05). The group's shape is the campaign's
 * `RCSData`, so a saved campaign and a saved webhook action both serialize it as they are.
 */

export const RCS_TEXT_CHARACTER_LIMIT = 5000;
export const RCS_CARD_TITLE_CHARACTER_LIMIT = 200;
export const RCS_CARD_DESCRIPTION_CHARACTER_LIMIT = 2000;
export const RCS_SUGGESTION_CHARACTER_LIMIT = 100;
export const RCS_MAX_SUGGESTIONS = 4;
export const RCS_MAX_CAROUSEL_CARDS = 3;

const MEDIA_TYPES: ReadonlyArray<string> = [RCSMessageTypeEnum.IMAGE, RCSMessageTypeEnum.VIDEO, RCSMessageTypeEnum.PDF];

type RcsSuggestion = { type?: string; title?: string; value?: string };

/** A carousel needs at least one card: an empty one would be saved and only refused at send time. */
export const rcsCarouselNotEmpty: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  (control as FormArray)?.length > 0 ? null : { required: true };

export function buildRcsSuggestionGroup(suggestion?: RcsSuggestion): FormGroup {
  return new FormGroup({
    type: new FormControl(suggestion?.type ?? '', [Validators.required]),
    title: new FormControl(suggestion?.title ?? '', [Validators.required]),
    value: new FormControl(suggestion?.value ?? '', [Validators.required]),
  });
}

/** A carousel card: every field is required, as it always was in the campaign wizard. */
export function buildRcsCardGroup(card?: Partial<RCSDataRichCard>): FormGroup {
  return new FormGroup({
    title: new FormControl(card?.title ?? '', [Validators.required, Validators.maxLength(RCS_CARD_TITLE_CHARACTER_LIMIT)]),
    description: new FormControl(card?.description ?? '', [Validators.required, Validators.maxLength(RCS_CARD_DESCRIPTION_CHARACTER_LIMIT)]),
    fileUrl: new FormControl(card?.fileUrl ?? null, [Validators.required]),
    fileContentType: new FormControl(card?.fileContentType ?? '', [Validators.required]),
    suggestions: new FormArray((card?.suggestions ?? []).map((s: RcsSuggestion) => buildRcsSuggestionGroup(s))),
  });
}

/**
 * The RCS message group, hydrated from a saved message when one is given — the suggestion and
 * carousel arrays included — with the validators of its format already applied. A carousel saved
 * without cards gets an empty one to fill, as the campaign wizard always did.
 */
export function buildRcsDataGroup(data?: Partial<RCSData>): FormGroup {
  const richCard = data?.richCard;
  const group = new FormGroup({
    type: new FormControl(data?.type ?? '', []),
    message: new FormControl(data?.message ?? '', [Validators.maxLength(RCS_TEXT_CHARACTER_LIMIT)]),
    url: new FormControl(data?.url ?? '', []),
    richCard: new FormGroup({
      title: new FormControl(richCard?.title ?? '', [Validators.maxLength(RCS_CARD_TITLE_CHARACTER_LIMIT)]),
      description: new FormControl(richCard?.description ?? '', [Validators.maxLength(RCS_CARD_DESCRIPTION_CHARACTER_LIMIT)]),
      fileUrl: new FormControl(richCard?.fileUrl ?? '', []),
      fileContentType: new FormControl(richCard?.fileContentType ?? '', []),
      suggestions: new FormArray((richCard?.suggestions ?? []).map((s: RcsSuggestion) => buildRcsSuggestionGroup(s))),
    }),
    carousel: new FormArray((data?.carousel ?? []).map((card) => buildRcsCardGroup(card))),
    agentType: new FormControl(data?.agentType ?? 'NON_CONVERSATIONAL', []),
  });

  if (data?.type === RCSMessageTypeEnum.CAROUSEL && (group.get('carousel') as FormArray).length === 0) {
    (group.get('carousel') as FormArray).push(buildRcsCardGroup());
  }

  applyRcsTypeValidators(group, { clear: false });
  return group;
}

/**
 * Applies the validators of the group's current format. With `clear` — a format the user just
 * picked — it also empties what belongs to the other formats: the text, the media address (on
 * EVERY change, so a file of the previous format never rides along), the rich card and the
 * carousel. Hydration runs it without `clear`, so a saved message keeps its content and still
 * gets the validators it needs.
 */
export function applyRcsTypeValidators(group: FormGroup, options: { clear: boolean }): void {
  const type = group.get('type')?.value;
  const message = group.get('message');
  const url = group.get('url');
  const richCard = group.get('richCard') as FormGroup;
  const carousel = group.get('carousel') as FormArray;
  const isRichCard = type === RCSMessageTypeEnum.RICH_CARD;

  message.setValidators(type === RCSMessageTypeEnum.TEXT
    ? [Validators.required, Validators.maxLength(RCS_TEXT_CHARACTER_LIMIT)]
    : [Validators.maxLength(RCS_TEXT_CHARACTER_LIMIT)]);
  url.setValidators(MEDIA_TYPES.includes(type) ? [Validators.required] : null);

  richCard.get('title').setValidators(isRichCard
    ? [Validators.required, Validators.maxLength(RCS_CARD_TITLE_CHARACTER_LIMIT)]
    : [Validators.maxLength(RCS_CARD_TITLE_CHARACTER_LIMIT)]);
  richCard.get('description').setValidators(isRichCard
    ? [Validators.required, Validators.maxLength(RCS_CARD_DESCRIPTION_CHARACTER_LIMIT)]
    : [Validators.maxLength(RCS_CARD_DESCRIPTION_CHARACTER_LIMIT)]);
  richCard.get('fileUrl').setValidators(isRichCard ? [Validators.required] : null);
  richCard.get('fileContentType').setValidators(isRichCard ? [Validators.required] : null);

  carousel.setValidators(type === RCSMessageTypeEnum.CAROUSEL ? [rcsCarouselNotEmpty] : null);

  if (options.clear) {
    if (type !== RCSMessageTypeEnum.TEXT) {
      message.setValue(null);
    }
    url.setValue(null);
    if (type !== RCSMessageTypeEnum.CAROUSEL) {
      carousel.clear();
    }
    if (!isRichCard) {
      (richCard.get('suggestions') as FormArray).clear();
      richCard.reset();
    }
  }

  for (const control of [message, url, richCard.get('title'), richCard.get('description'), richCard.get('fileUrl'), richCard.get('fileContentType'), carousel]) {
    control.updateValueAndValidity();
  }
}

/** The message type an attendance records for an RCS format — the twin of the backend mapping. */
export function rcsMessageType(type: string): MessageTypeEnum {
  if (type === RCSMessageTypeEnum.IMAGE) return MessageTypeEnum.image;
  if (type === RCSMessageTypeEnum.VIDEO) return MessageTypeEnum.video;
  if (type === RCSMessageTypeEnum.PDF) return MessageTypeEnum.document;
  return MessageTypeEnum.text;
}

/** `title — description` for a rich card; the card titles for a carousel — the backend's summary. */
export function rcsSummary(data: Partial<RCSData>): string {
  if (data?.type === RCSMessageTypeEnum.CAROUSEL) {
    return (data.carousel ?? []).map((card) => card?.title?.trim()).filter(Boolean).join(' · ');
  }
  return [data?.richCard?.title?.trim(), data?.richCard?.description?.trim()].filter(Boolean).join(' — ');
}

/**
 * The plain `value` a webhook action keeps next to its `rcsData`: the text, the media address, or
 * the summary. An older backend reads it, so a rollback still sends something sensible (plan AD-10).
 */
export function rcsValueMirror(data: Partial<RCSData>): string {
  if (data?.type === RCSMessageTypeEnum.TEXT) return data.message ?? '';
  if (MEDIA_TYPES.includes(data?.type)) return data.url ?? '';
  return rcsSummary(data);
}

/**
 * A webhook action saved before the RCS editor — plain text, or media by address — turned into the
 * editor's shape when it is opened again (plan AD-10). It keeps its old shape until it is saved.
 */
export function legacyActionToRcsData(action: { typeMessage?: string; value?: unknown }): Partial<RCSData> {
  const value = typeof action?.value === 'string' ? action.value : '';
  switch (action?.typeMessage) {
    case MessageTypeEnum.image:
      return { type: RCSMessageTypeEnum.IMAGE, url: value };
    case MessageTypeEnum.video:
      return { type: RCSMessageTypeEnum.VIDEO, url: value };
    case MessageTypeEnum.document:
      return { type: RCSMessageTypeEnum.PDF, url: value };
    default:
      return { type: RCSMessageTypeEnum.TEXT, message: value };
  }
}
